import { createClient } from "@supabase/supabase-js";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database } from "@/integrations/supabase/types";

export type EcosystemPartner = {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  website_url: string;
  logo_url: string | null;
  published: boolean;
  nofollow: boolean;
  sponsored: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

export const UGC_KING_FALLBACK: EcosystemPartner = {
  id: "ugc-king-fallback",
  name: "UGC King",
  slug: "ugc-king",
  category: "AI content production",
  description:
    "UGC King is your AI social media team on autopilot. It autonomously researches trends, writes and films AI UGC videos, and posts to TikTok, Instagram, and Facebook, then reads the analytics and improves over time.",
  website_url: "https://ugcking.com",
  logo_url: null,
  published: true,
  nofollow: true,
  sponsored: true,
  sort_order: 0,
};

export const CLOVIA_FALLBACK: EcosystemPartner = {
  id: "clovia-fallback",
  name: "Clovia",
  slug: "clovia",
  category: "Checkout diagnostics partner",
  description:
    "Clovia is checkout analytics for Shopify. It records every checkout session and shows merchants exactly where revenue leaks: payment errors, broken buttons, shipping failures, invalid discount codes.",
  website_url: "https://clovia.app",
  logo_url: null,
  published: true,
  nofollow: true,
  sponsored: false,
  sort_order: 1,
};

// Keep the public directory renderable before the one-time storage migration is applied.
const FALLBACK_PARTNERS: EcosystemPartner[] = [UGC_KING_FALLBACK, CLOVIA_FALLBACK];

function publicSupabase() {
  const url = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
  const key =
    process.env.SUPABASE_PUBLISHABLE_KEY || process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("Missing Supabase public configuration");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

function ecosystemTable(client: unknown): any {
  return (client as any).from("ecosystem_partners");
}

function isStorageMissing(error: unknown): boolean {
  const message =
    typeof error === "object" && error && "message" in error
      ? String((error as { message?: unknown }).message ?? "")
      : String(error ?? "");
  return (
    message.includes("ecosystem_partners") &&
    (message.includes("does not exist") ||
      message.includes("schema cache") ||
      message.includes("relation"))
  );
}

async function requireAdmin(context: { supabase: any; userId: string }) {
  const { data, error } = await context.supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", context.userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

const SlugValue = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9-]+$/, "Use lowercase letters, numbers, and hyphens only");

const PartnerInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(120),
  slug: SlugValue,
  category: z.string().trim().min(1).max(100),
  description: z.string().trim().min(1).max(1200),
  website_url: z.string().trim().url().max(500),
  logo_url: z.string().trim().max(1000).nullable().optional(),
  published: z.boolean().default(true),
  nofollow: z.boolean().default(true),
  sponsored: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(10000).default(0),
});

const IdSchema = z.object({ id: z.string().uuid() });
export type EcosystemPartnerInput = z.infer<typeof PartnerInputSchema>;

export const listPublicEcosystemPartners = createServerFn({ method: "GET" }).handler(
  async () => {
    try {
      const { data, error } = await ecosystemTable(publicSupabase())
        .select(
          "id, name, slug, category, description, website_url, logo_url, published, nofollow, sponsored, sort_order, updated_at",
        )
        .eq("published", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) {
        if (!isStorageMissing(error)) {
          console.error("listPublicEcosystemPartners error", error);
        }
        return { partners: FALLBACK_PARTNERS, storageReady: false };
      }
      return { partners: (data ?? []) as EcosystemPartner[], storageReady: true };
    } catch (error) {
      console.error("listPublicEcosystemPartners exception", error);
      return { partners: FALLBACK_PARTNERS, storageReady: false };
    }
  },
);

export const listAdminEcosystemPartners = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context);
    const { data, error } = await ecosystemTable(supabaseAdmin)
      .select("*")
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      if (isStorageMissing(error)) {
        return { partners: FALLBACK_PARTNERS, storageReady: false };
      }
      throw new Error(error.message);
    }
    return { partners: (data ?? []) as EcosystemPartner[], storageReady: true };
  });

export const upsertEcosystemPartner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PartnerInputSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const payload = {
      name: data.name,
      slug: data.slug,
      category: data.category,
      description: data.description,
      website_url: data.website_url,
      logo_url: data.logo_url || null,
      published: data.published,
      nofollow: data.nofollow,
      sponsored: data.sponsored,
      sort_order: data.sort_order,
      updated_at: new Date().toISOString(),
    };

    if (data.id) {
      const { data: row, error } = await ecosystemTable(supabaseAdmin)
        .update(payload)
        .eq("id", data.id)
        .select("id")
        .single();
      if (error) {
        if (isStorageMissing(error)) {
          throw new Error(
            "Partner storage is not installed yet. Apply the ecosystem_partners migration first.",
          );
        }
        throw new Error(error.message);
      }
      return { id: row.id as string };
    }

    const { data: row, error } = await ecosystemTable(supabaseAdmin)
      .insert(payload)
      .select("id")
      .single();
    if (error) {
      if (isStorageMissing(error)) {
        throw new Error(
          "Partner storage is not installed yet. Apply the ecosystem_partners migration first.",
        );
      }
      throw new Error(error.message);
    }
    return { id: row.id as string };
  });

export const deleteEcosystemPartner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { error } = await ecosystemTable(supabaseAdmin).delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
